/**
 * Color Utility Functions
 *
 * Provides color manipulation utilities for category-based class coloring
 * with fallback to hash-based colors for uncategorized classes.
 */

import { Category } from '@/types'

/**
 * Hash-based color generation for classes without categories
 * Generates a consistent color based on the class name string
 */
export function getHashBasedColor(className: string): string {
  // Simple hash function to generate a consistent color from string
  let hash = 0
  for (let i = 0; i < className.length; i++) {
    hash = className.charCodeAt(i) + ((hash << 5) - hash)
  }

  // Convert hash to hue (0-360)
  const hue = Math.abs(hash) % 360

  // Use HSL with fixed saturation and lightness for good visibility
  // Saturation: 70% for vibrant colors
  // Lightness: 50% for good contrast
  return `hsl(${hue}, 70%, 50%)`
}

/**
 * Get color for a class, using category color if available,
 * otherwise falling back to hash-based color
 */
export function getClassColor(
  className: string,
  category?: Category | null
): string {
  if (category?.color) {
    return category.color
  }
  return getHashBasedColor(className)
}

/**
 * Convert hex color to RGB components
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Parse hex values
  if (hex.length === 3) {
    // Short form: #RGB -> #RRGGBB
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('')
  }

  if (hex.length !== 6) {
    console.warn(`Invalid hex color: ${hex}`)
    return null
  }

  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return { r, g, b }
}

/**
 * Convert hex color to RGBA with specified opacity
 */
export function hexToRgba(hex: string, opacity: number): string {
  const rgb = hexToRgb(hex)

  if (!rgb) {
    // Fallback to semi-transparent gray
    return `rgba(128, 128, 128, ${opacity})`
  }

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
}

/**
 * Generate color with opacity (works with both hex and HSL)
 */
export function colorWithOpacity(color: string, opacity: number): string {
  // If it's a hex color, convert to rgba
  if (color.startsWith('#')) {
    return hexToRgba(color, opacity)
  }

  // If it's HSL, convert to HSLA
  if (color.startsWith('hsl(')) {
    return color.replace('hsl(', 'hsla(').replace(')', `, ${opacity})`)
  }

  // If it's already rgba or hsla, return as is
  return color
}

/**
 * Check if a color is light (for determining text color)
 */
export function isLightColor(hex: string): boolean {
  const rgb = hexToRgb(hex)

  if (!rgb) {
    return false
  }

  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255

  return luminance > 0.5
}

/**
 * Get contrasting text color (black or white) for a background color
 */
export function getContrastTextColor(backgroundColor: string): string {
  if (backgroundColor.startsWith('#')) {
    return isLightColor(backgroundColor) ? '#000000' : '#FFFFFF'
  }

  // For HSL colors, assume dark text for light backgrounds
  // This is a simple heuristic - could be improved
  return '#FFFFFF'
}
